<template>
  <teleport to="body">
    <div class="modal-overlay" @click.self="$emit('close')">
      <div class="modal-container" @click.stop>
        <div class="modal-header">
          <h2>Visualizzazione Rapida</h2>
          <button class="btn-close" @click="$emit('close')" title="Chiudi">×</button>
        </div>

        <div class="modal-body">
          <p class="modal-description">
            Seleziona un grafico per visualizzarlo temporaneamente in cima alla lista.
          </p>

          <div class="select-group">
            <label>Grafico Singolo</label>
            <div class="input-row">
              <select v-model="selectedSensor" class="custom-select">
                <option value="">Seleziona sensore...</option>
                <option v-for="s in availableSensors" :key="s" :value="s">
                  {{ SENSOR_LABELS[s] || s }}
                </option>
              </select>
              <button 
                class="btn-action" 
                @click="confirm(selectedSensor)" 
                :disabled="!selectedSensor"
              >
                <FontAwesomeIcon :icon="faEye" />
              </button>
            </div>
          </div>

          <div class="select-group">
            <label>Grafico Combinato</label>
            <div class="input-row">
              <select v-model="selectedCross" class="custom-select">
                <option value="">Seleziona combinato...</option>
                <option v-for="c in availableCross" :key="c.id" :value="c.id">
                  {{ c.title }}
                </option>
              </select>
              <button 
                class="btn-action" 
                @click="confirm(selectedCross)" 
                :disabled="!selectedCross"
              >
                <FontAwesomeIcon :icon="faEye" />
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useChartVisibility } from '@/composables/useChartVisibility';
import { SENSOR_LABELS, SENSOR_LIST, CROSS_LIST } from '@/config/chartConfig';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';

const emit = defineEmits<{
  (e: 'close'): void;
}>();

// Lock body scroll
onMounted(() => {
  document.body.style.overflow = 'hidden';
});

onBeforeUnmount(() => {
  document.body.style.overflow = '';
});

const { preferences, setTemporaryChart } = useChartVisibility();

const selectedSensor = ref("");
const selectedCross = ref("");

// Show all sensors/charts, or maybe filter out those already visible?
// "Possibilità di selezionare rapidamente un grafico che non è tra i preferiti"
// implies we should primarily target those NOT in orderedCharts. 
// But being able to jump to one already there is also fine. 
// Let's filter to make it easier to find missing ones, as per implicit goal.
const availableSensors = computed(() => {
  return SENSOR_LIST.filter(s => !preferences.value.orderedCharts.includes(s));
});

const availableCross = computed(() => {
  return CROSS_LIST.filter(c => !preferences.value.orderedCharts.includes(c.id));
});

const confirm = (id: string) => {
  if (!id) return;
  setTemporaryChart(id);
  emit('close');
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100dvh;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: flex-start; /* Better for mobile */
  padding: 16px;
  padding-top: max(16px, env(safe-area-inset-top));
  box-sizing: border-box;
  z-index: 99999;
}

@media (min-width: 768px) {
  .modal-overlay {
    align-items: center;
    padding-top: 20px;
  }
}

.modal-container {
  width: min(500px, 100%);
  max-height: calc(100dvh - 32px);
  margin: 0 auto;
  background: #1a1a1a;
  border-radius: 12px;
  border: 1px solid #2a2a2a;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #2a2a2a;
}

.modal-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #e6e6e6;
}

.btn-close {
  background: transparent;
  border: none;
  color: #999;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.btn-close:hover {
  color: #4db8a8;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
}

.modal-description {
  margin: 0 0 24px 0;
  color: #9fb0c2;
  font-size: 14px;
  line-height: 1.5;
}

.select-group {
  margin-bottom: 20px;
}

.select-group label {
  display: block;
  font-size: 13px;
  color: #9fb0c2;
  font-weight: 500;
  margin-bottom: 8px;
}

.input-row {
  display: flex;
  gap: 10px;
}

.custom-select {
  flex: 1;
  padding: 10px;
  background: #252525;
  border: 1px solid #333;
  border-radius: 8px;
  color: #e6e6e6;
  font-size: 14px;
  cursor: pointer;
  outline: none;
}

.custom-select:focus {
  border-color: #4db8a8;
}

.btn-action {
  width: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(77, 184, 168, 0.1);
  border: 1px solid #4db8a8;
  color: #4db8a8;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-action:hover:not(:disabled) {
  background: rgba(77, 184, 168, 0.2);
}

.btn-action:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  border-color: #444;
  color: #666;
}
</style>
